import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import AddressGroupV2 from 'components/field/AddressGroupV2';
import AutoComplete from 'components/field/AutoComplete';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';

import useLocale from 'hooks/useLocale';

import { AUTOCOMPLETE } from 'services/consts';

import * as S from '../QuickShipment.styled';

const defaultColSpan = {
	md: 6
};

const ReceiverDetails = ({ formMode }) => {
	const { Country } = useLocale();

	const formMethods = useFormContext();

	const {
		setValue,
		watch,
		formState: { isLoading: isFormLoading, isSubmitting }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const autoFillCustomerDetailsLabels = useMemo(() => ['fullName'], []);
	const autoFillCustomerDetailsColumns = useMemo(
		() => [
			{ accessor: 'firstName', width: 150 },
			{ accessor: 'lastName', width: 150 },
			{ accessor: 'phone', width: 150 }
		],
		[]
	);

	const setMultipleValues = useCallback(
		(values) => {
			Object.entries(values).forEach(([key, value]) => {
				setValue(key, value, { shouldValidate: true, shouldDirty: true });
			});
		},
		[setValue]
	);
	return (
		<S.CreateShipmentContent>
			<S.Title variant="p2" fontWeight="500">
				contactDetails
			</S.Title>
			<S.CreateShipmentContentWrapper>
				<FormItem colSpan={defaultColSpan} label="fullName" isLoading={isLoading} required>
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.AUTOFILL_CUSTOMER_DETAILS}
						name="fullName"
						value={watch('autofillID')}
						customValue={watch('fullName')}
						setValue={(value) => {
							if (value === null) {
								setMultipleValues({
									autofillID: null,
									fullName: null
								});
								return;
							}
							if (watch('autofillID') !== value?.ID) {
								setMultipleValues({
									autofillID: value?.ID ?? null,
									fullName: value?.firstName ? `${value.firstName} ${value.lastName}` : null,
									phone: value?.phone ?? null,
									email: value?.email ?? null,
									'address.text': value?.text ?? null,
									'address.shortAddressCode': value?.shortAddressCode ?? null,
									'address.countryName': value?.countryName ?? Country,
									'address.cityName': value?.cityName ?? null,
									'address.stateName': value?.stateName ?? null,
									'address.districtName': value?.districtName ?? null,
									'address.buildingName': value?.buildingName ?? null,
									'address.addressInputType': value?.addressInputType ?? 'fullTextAddress',
									'address.street': value?.street ?? null,
									'address.lat': value?.lat ?? null,
									'address.lng': value?.lng ?? null,
									'address.zipCode': value?.zipCode ?? null
								});
							}
						}}
						textValue={watch('fullName')}
						setTextValue={(value) => {
							setMultipleValues({
								autofillID: null,
								fullName: value
							});
						}}
						columns={autoFillCustomerDetailsColumns}
						selectionLabelAccessor={autoFillCustomerDetailsLabels}
						fixedContainerWidth={false}
					/>
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="email" isLoading={isLoading}>
					<StringInput name="email" />
				</FormItem>

				<FormItem colSpan={defaultColSpan} label="phoneNumber" isLoading={isLoading} required>
					<PhoneNumber
						name="phone"
						value={watch('phone')}
						onChange={(e) => {
							setValue('phone', e, { shouldValidate: true, shouldDirty: true });
						}}
					/>
				</FormItem>
			</S.CreateShipmentContentWrapper>
			<S.Line />
			<S.Title variant="p2" fontWeight="500">
				addressDetails
			</S.Title>

			<AddressGroupV2
				formFieldName="address"
				isLoading={isLoading}
				formMode={formMode}
				autoFillID={watch('autofillID')}
			/>
		</S.CreateShipmentContent>
	);
};

export default ReceiverDetails;
